import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { MapContainer, useMap } from 'react-leaflet';
import { FeatureCollection, GeoJsonObject, Feature } from 'geojson';
import L from 'leaflet';
import { Layout, Breadcrumb, Button, Tooltip } from 'antd';

import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  getFile,
  exportAsBuilt,
  getDownloadUrl,
} from '../../../Network/Core/Directory/DirectoryServices';

//Components
import { Sidebar } from '../../../Components/Sidebar/Sidebar';
import { HeaderBar } from '../../../Components/Header/Header';

//API
import axios from 'axios';

//CSS
import './ProjectDetail.css';
import './Map.css';
import AuthUser from '../../../Utils/user';
import { refreshCloudFrontCookies } from '../../../Utils';
import Loader from '../../../Components/CoreComponents/Loader';
import { toast } from 'react-toastify';

// S3 bucket name for new service files (used for path detection)
const DWG_FILES_BUCKET = process.env.REACT_APP_DWG_FILES_BUCKET || 'dwgtogeojson-files-dev';

const { Content } = Layout;

interface LayerElement {
  name: string;
  color: string;
  visible: boolean;
  category: string;
}

interface Data extends FeatureCollection {
  layers: LayerElement[];
}

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonObject
  | Array<JsonValue>;
interface JsonObject {
  [key: string]: JsonValue;
}

// Define an interface for RGB values
interface RGB {
  r: number;
  g: number;
  b: number;
}

// Define an interface for your layer data
interface Layer {
  name: string;
  color: string;
}

// Helper: convert "#RRGGBB" → { r, g, b }
function hexToRgb(hex: string): RGB {
  const clean = hex.replace(/^#/, '');
  const bigint = Number.parseInt(clean, 16);
  return {
    r: (bigint >> 16) & 0xff,
    g: (bigint >> 8) & 0xff,
    b: bigint & 0xff,
  };
}

// Helper: compute perceived luminance (0..255)
function getLuminance(rgb: RGB): number {
  return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
}

// Helper: invert "#RRGGBB" → "#(FFFFFF ⊕ RRGGBB)"
function invertHex(hex: string): string {
  const clean = hex.replace(/^#/, '');
  const invertedInt = 0xffffff ^ Number.parseInt(clean, 16);
  const invertedHex = invertedInt.toString(16).padStart(6, '0');
  return `#${invertedHex}`;
}

// Helper: Get optimized color (memoization-friendly)
function getOptimizedColor(color: string, threshold = 50): string {
  const rgb = hexToRgb(color);
  let result = getLuminance(rgb) < threshold ? invertHex(color) : color;
  // Special case fix
  if (result === '#45230A') result = '#a17d62';
  return result;
}

// Function to normalize coordinates
const normalizeCoordinates = (geojson: Data): Data => {
  // Find bounding box
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  const updateBounds = (coordinates: [number, number, number?][]) => {
    coordinates.forEach(([x, y]) => {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    });
  };

  geojson.features.forEach((feature) => {
    if (!feature.geometry) return; // Skip features with null geometry
    switch (feature.geometry.type) {
      case 'LineString':
        updateBounds(
          feature.geometry.coordinates as [number, number, number?][],
        );
        break;
      case 'Point':
        updateBounds([
          feature.geometry.coordinates as [number, number, number?],
        ]);
        break;
      case 'MultiPoint':
        updateBounds(
          feature.geometry.coordinates as [number, number, number?][],
        );
        break;
      case 'MultiLineString':
        feature.geometry.coordinates.forEach((line) =>
          updateBounds(line as [number, number, number?][]),
        );
        break;
    }
  });

  const safeRange = (val: number) => (val === 0 ? 1 : val);

  // Translate and scale coordinates to fit into [-180, 180] and [-90, 90]
  const rangeX = maxX - minX;
  const rangeY = maxY - minY;
  const scaleX = 360 / safeRange(rangeX);
  const scaleY = 180 / safeRange(rangeY);
  const scale = Math.min(scaleX, scaleY) * 0.001;

  // Calculate center point for translation
  const centerX = minX + rangeX / 2;
  const centerY = minY + rangeY / 2;

  const translateAndScale = ([x, y, z]: [number, number, number?]): [
    number,
    number,
    number?,
  ] => [(x - centerX) * scale, (y - centerY) * scale, z];

  const normalizeFeature = (feature: any): any => {
    if (!feature.geometry) return feature; // Skip features with null geometry

    // Store the original coordinates in a new property
    feature.properties.originalCoordinates = feature.geometry.coordinates;

    switch (feature.geometry.type) {
      case 'LineString':
        feature.geometry.coordinates =
          feature.geometry.coordinates.map(translateAndScale);
        break;
      case 'Point':
        feature.geometry.coordinates = translateAndScale(
          feature.geometry.coordinates,
        );
        break;
      case 'MultiPoint':
        feature.geometry.coordinates =
          feature.geometry.coordinates.map(translateAndScale);
        break;
      case 'MultiLineString':
        feature.geometry.coordinates = feature.geometry.coordinates.map(
          (line: [number, number, number?][]) => line.map(translateAndScale),
        );
        break;
    }
    return feature;
  };

  const normalizedGeojson = { ...geojson };
  normalizedGeojson.features = normalizedGeojson.features
    .filter((f) => f.geometry && f.properties && !f.properties.removed)
    .map(normalizeFeature);
  return normalizedGeojson;
};

// ============================================================================
// OPTIMIZED MyData Component with Canvas rendering and Layer Groups
// ============================================================================
const MyData = ({ file }: any) => {
  if (!file) {
    return null;
  }

  const [data, setData] = useState<Data | null>(null);
  const [layerVisibility, setLayerVisibility] = useState<
    Record<string, boolean>
  >({});
  const [loaded, setLoaded] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const map = useMap();

  // Refs for optimization
  const layerGroupsRef = useRef<globalThis.Map<string, L.LayerGroup>>(
    new globalThis.Map(),
  );
  const fetchingRef = useRef<boolean>(false);
  const lastFileIdRef = useRef<string | null>(null);
  const canvasRendererRef = useRef<L.Renderer | null>(null);

  // Initialize Canvas renderer once
  useEffect(() => {
    if (!canvasRendererRef.current) {
      canvasRendererRef.current = L.canvas({ tolerance: 3 });
    }
  }, []);

  /**
   * Check if a path is from the new service (S3 URI format)
   */
  const isNewServicePath = (path: string | undefined): boolean => {
    return !!path?.startsWith(`s3://${DWG_FILES_BUCKET}`);
  };

  /**
   * Convert S3 URI to CloudFront URL
   * Uses signed COOKIES for authentication (set during login)
   * This allows browser caching since URL is always the same
   */
  const getCloudFrontUrl = (s3Uri: string | undefined): string | null => {
    const cdnUrl = process.env.REACT_APP_FILES_CDN_URL || '';
    if (!s3Uri || !cdnUrl) return null;

    // s3://bucket/path/to/file.geojson -> https://cdn.url/path/to/file.geojson
    const s3Prefix = `s3://${DWG_FILES_BUCKET}/`;
    if (s3Uri.startsWith(s3Prefix)) {
      const path = s3Uri.substring(s3Prefix.length);
      return `${cdnUrl}/${path}`;
    }
    return null;
  };

  /**
   * Get URL for old service files (direct S3 URLs - public)
   */
  const getOldServiceUrl = (path: string | undefined): string | null => {
    if (!path) return null;
    if (path.startsWith('https://')) return path;
    return null;
  };

  /**
   * Fetch signed URL from backend for a file (used for downloads, Android)
   */
  const fetchSignedUrl = async (
    fileId: string,
    fileType: 'geojson' | 'dwg',
  ): Promise<string | null> => {
    try {
      const response = await getDownloadUrl(fileId, fileType);
      if (response?.data?.downloadUrl) {
        return response.data.downloadUrl;
      }
      console.warn(`[Drawing] Failed to get signed URL for ${fileType}`);
      return null;
    } catch (error) {
      console.error(
        `[Drawing] Error fetching signed URL for ${fileType}:`,
        error,
      );
      return null;
    }
  };

  /**
   * Fetch signed URL for as-built file from backend
   */
  const fetchAsBuiltSignedUrl = async (
    fileId: string,
  ): Promise<string | null> => {
    try {
      // Use a special type for as-built or fetch it differently
      const response = await getDownloadUrl(fileId, 'asbuilt');
      if (response?.data?.downloadUrl) {
        return response.data.downloadUrl;
      }
      return null;
    } catch (error) {
      console.error('[Drawing] Error fetching as-built signed URL:', error);
      return null;
    }
  };

  // Memoized layer colors calculation
  const layerColors = useMemo(() => {
    if (!data?.layers) return {};
    return data.layers.reduce((acc: Record<string, string>, layer: Layer) => {
      acc[layer.name] = getOptimizedColor(layer.color);
      return acc;
    }, {});
  }, [data?.layers]);

  // Memoized style function for GeoJSON features
  const getFeatureStyle = useCallback(
    (feature: any, isAsBuilt: boolean) => {
      const layerName = feature.properties?.layer;
      let color = feature.properties?.color || layerColors[layerName] || '#000';
      color = getOptimizedColor(color);

      return {
        weight: isAsBuilt ? 3.5 : 2,
        opacity: 1,
        color: color,
        interactive: true,
        renderer: canvasRendererRef.current!,
      };
    },
    [layerColors],
  );

  // Memoized point to layer function
  const createPointMarker = useCallback(
    (feature: any, latlng: L.LatLng) => {
      const layer = feature.properties?.layer;
      let color = feature.properties?.color || layerColors[layer] || '#000';
      color = getOptimizedColor(color);

      return L.circleMarker(latlng, {
        radius: 2,
        fillColor: color,
        color: '#fff',
        weight: 1,
        opacity: 1,
        fillOpacity: 1,
        renderer: canvasRendererRef.current!, // Use Canvas renderer
      });
    },
    [layerColors],
  );

  // Add features in batches to prevent UI blocking
  const addFeaturesInBatches = useCallback(
    async (
      features: Feature[],
      layerGroup: L.LayerGroup,
      isAsBuilt: boolean,
      batchSize = 500,
    ) => {
      const total = features.length;

      for (let i = 0; i < total; i += batchSize) {
        const batch = features.slice(i, i + batchSize);

        batch.forEach((feature) => {
          if (!feature.geometry) return;

          const options: L.GeoJSONOptions = {
            style: () => getFeatureStyle(feature, isAsBuilt),
            pointToLayer: (f, latlng) => createPointMarker(f, latlng),
            // LAZY POPUP: Only create popup on click, not for every feature
            onEachFeature: (f, layer) => {
              layer.on('click', (e: any) => {
                const layerName = f.properties?.layer || 'Unknown';
                let coord: number[] | undefined;

                if (
                  f.geometry?.type === 'Point' &&
                  Array.isArray(f.properties?.originalCoordinates)
                ) {
                  coord = f.properties.originalCoordinates;
                } else if (
                  f.geometry?.type === 'MultiPoint' &&
                  Array.isArray(f.properties?.originalCoordinates) &&
                  typeof (layer as any).getLayers === 'function'
                ) {
                  // Leaflet expands MultiPoint into a FeatureGroup of markers.
                  // Children preserve coordinate order, so the clicked child's
                  // index maps directly to originalCoordinates[index].
                  const children = (layer as L.FeatureGroup).getLayers();
                  const idx = children.indexOf(e.sourceTarget);
                  if (idx >= 0) {
                    coord = f.properties.originalCoordinates[idx];
                  }
                }

                const coordStr = Array.isArray(coord)
                  ? ` (${coord.map((n: number) => n.toFixed(2)).join(', ')})`
                  : '';

                // Open the popup on the specific clicked marker (for MultiPoint
                // this is one child of the FeatureGroup, not the group centroid).
                const popupTarget =
                  e.sourceTarget &&
                  typeof e.sourceTarget.bindPopup === 'function'
                    ? e.sourceTarget
                    : layer;
                popupTarget
                  .bindPopup(`${layerName}${coordStr}`)
                  .openPopup();
              });
            },
          };

          const geoLayer = L.geoJSON(feature as GeoJsonObject, options);
          layerGroup.addLayer(geoLayer);
        });

        // Update progress
        setLoadingProgress(
          Math.min(100, Math.round(((i + batchSize) / total) * 100)),
        );

        // Yield to UI thread every batch
        if (i + batchSize < total) {
          await new Promise((resolve) => requestAnimationFrame(resolve));
        }
      }
    },
    [getFeatureStyle, createPointMarker],
  );

  // Build layer groups when data changes
  useEffect(() => {
    if (!data || !map) return;

    let isCancelled = false;

    const buildLayerGroups = async () => {
      // Clear existing layer groups
      layerGroupsRef.current.forEach((group) => {
        if (map.hasLayer(group)) {
          map.removeLayer(group);
        }
        group.clearLayers();
      });
      layerGroupsRef.current.clear();

      // Group features by layer name
      const featuresByLayer = new globalThis.Map<string, Feature[]>();
      data.features.forEach((feature) => {
        const layerName = feature.properties?.layer;
        if (!layerName) return;
        if (!featuresByLayer.has(layerName)) {
          featuresByLayer.set(layerName, []);
        }
        featuresByLayer.get(layerName)!.push(feature);
      });

      // Create a layer group for each layer
      let processedLayers = 0;
      const totalLayers = data.layers.length;

      for (const layer of data.layers) {
        // Check if effect was cancelled (component unmounted)
        if (isCancelled) return;

        const features = featuresByLayer.get(layer.name) || [];
        if (features.length === 0) continue;

        const layerGroup = L.layerGroup();
        const isAsBuilt = layer.category?.includes('As-Built') || false;

        // Add features in batches
        await addFeaturesInBatches(features, layerGroup, isAsBuilt);

        // Check again after async operation
        if (isCancelled) return;

        layerGroupsRef.current.set(layer.name, layerGroup);

        // Add to map if visible and map is still valid
        if (layerVisibility[layer.name] && map.getContainer()) {
          try {
            layerGroup.addTo(map);
          } catch (e) {
            console.warn('[Map] Failed to add layer, map may be unmounted');
          }
        }

        processedLayers++;
      }

      if (!isCancelled) {
        setLoaded(true);
        setLoadingProgress(100);
      }
    };

    buildLayerGroups();

    // Cleanup function to cancel async operation
    return () => {
      isCancelled = true;
    };
  }, [data, map, addFeaturesInBatches]); // Note: layerVisibility intentionally excluded

  // Handle layer visibility toggle - O(1) operation with layer groups
  useEffect(() => {
    if (!map || layerGroupsRef.current.size === 0 || !map.getContainer())
      return;

    layerGroupsRef.current.forEach((group, layerName) => {
      const shouldBeVisible = layerVisibility[layerName];
      const isCurrentlyVisible = map.hasLayer(group);

      try {
        if (shouldBeVisible && !isCurrentlyVisible) {
          group.addTo(map);
        } else if (!shouldBeVisible && isCurrentlyVisible) {
          map.removeLayer(group);
        }
      } catch (e) {
        console.warn('[Map] Failed to toggle layer visibility');
      }
    });
  }, [layerVisibility, map]);

  // Fetch and process data
  useEffect(() => {
    if (fetchingRef.current || lastFileIdRef.current === file.id) {
      return;
    }

    const getData = async () => {
      fetchingRef.current = true;
      setLoadingProgress(0);

      // Determine URLs for drawing and as-built
      let drawingUrl: string | null = null;
      let asBuiltUrl: string | null = null;

      // Prefer simplified version (smaller, faster to load) over full version
      const geojsonPath = file.simplifiedGeojson || file.geojsonPath;

      // Cache buster for CloudFront - ensures fresh file after conversions
      // CloudFront cache policy includes 'v' param in cache key
      // Use milliseconds - no special characters, no encoding issues
      // Must match backend format: new Date(timestamp).getTime()
      const timestamp = file.updatedAt || file.createdAt;
      const cacheKey = timestamp ? new Date(timestamp).getTime() : Date.now();
      const cacheBuster = `?v=${cacheKey}`;

      // Determine URL based on file path type
      if (isNewServicePath(geojsonPath)) {
        // NEW SERVICE: Convert S3 URI to CloudFront URL (uses signed cookies)
        const baseUrl = getCloudFrontUrl(geojsonPath);
        drawingUrl = baseUrl ? `${baseUrl}${cacheBuster}` : null;
      } else {
        // OLD SERVICE: Use direct S3 URL (public)
        drawingUrl = getOldServiceUrl(geojsonPath);
      }

      // Handle as-built URL
      if (isNewServicePath(file.asBuilt)) {
        // NEW SERVICE: Convert S3 URI to CloudFront URL (uses signed cookies)
        const baseUrl = getCloudFrontUrl(file.asBuilt);
        asBuiltUrl = baseUrl ? `${baseUrl}${cacheBuster}` : null;
      } else {
        // OLD SERVICE: Use direct S3 URL (public)
        asBuiltUrl = getOldServiceUrl(file.asBuilt);
      }

      if (!drawingUrl) {
        console.error('No drawing URL available');
        setLoaded(true);
        fetchingRef.current = false;
        return;
      }

      setLoadingProgress(20);

      // Fetch data - use credentials only for CloudFront URLs (new service)
      // Old S3 URLs are public and return Access-Control-Allow-Origin: * which doesn't work with credentials
      const FILES_CDN_URL = process.env.REACT_APP_FILES_CDN_URL || '';
      const needsCredentials = (url: string) =>
        url.startsWith(FILES_CDN_URL) && FILES_CDN_URL !== '';

      const drawingConfig = needsCredentials(drawingUrl)
        ? { withCredentials: true }
        : {};
      const response = await axios.get(drawingUrl, drawingConfig);
      setLoadingProgress(50);

      const asBuiltConfig =
        asBuiltUrl && needsCredentials(asBuiltUrl)
          ? { withCredentials: true }
          : {};
      const asBuiltResponse = asBuiltUrl
        ? await axios.get(asBuiltUrl, asBuiltConfig)
        : { data: { features: [], layers: [] } };
      setLoadingProgress(70);

      processGeoJSONData(response.data, asBuiltResponse.data);
    };

    const processGeoJSONData = (drawingData: any, asBuiltData: any) => {
      // Ensure data structures exist
      if (!asBuiltData.layers) asBuiltData.layers = [];
      if (!asBuiltData.features) asBuiltData.features = [];
      drawingData.layers = drawingData.layers || [];

      // Merge features from both datasets
      const combinedFeatures = [
        ...drawingData.features,
        ...asBuiltData.features,
      ];
      const combinedLayers = [...drawingData.layers, ...asBuiltData.layers];

      // console.log(
      //   `[Drawing] Processing ${combinedFeatures.length} features, ${combinedLayers.length} layers`,
      // );

      const combinedData: Data = normalizeCoordinates({
        ...drawingData,
        features: combinedFeatures,
        layers: combinedLayers,
      });

      // Initialize layer visibility
      const initialLayerVisibility: Record<string, boolean> =
        combinedLayers.reduce((acc: Record<string, boolean>, layer: any) => {
          acc[layer.name] = layer.visible;
          return acc;
        }, {});

      setLayerVisibility(initialLayerVisibility);
      setData(combinedData);

      // Fit map to bounds - guard against map not being ready
      const geojsonObject = L.geoJSON(combinedData as GeoJsonObject);
      try {
        // Check if map is ready before calling methods
        if (!map || !map.getContainer()) {
          console.warn('[Map] Map not ready, skipping bounds fit');
          lastFileIdRef.current = file.id;
          return;
        }

        const bounds = geojsonObject.getBounds();
        if (bounds.isValid()) {
          if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
            map.setView(bounds.getCenter(), 17);
          } else {
            map.fitBounds(bounds.pad(0.1));
          }
        } else {
          map.setView([0, 0], 2);
        }
        lastFileIdRef.current = file.id;
      } catch (e) {
        // Guard against map not being ready
        if (map && map.getContainer()) {
          try {
            map.fitWorld();
          } catch (mapError) {
            console.warn('[Map] Failed to fitWorld:', mapError);
          }
        }
      } finally {
        fetchingRef.current = false;
      }
    };

    getData().catch(async (err) => {
      // Check if this is a 403 from CloudFront (expired cookies)
      if (err?.response?.status === 403) {
        console.info(
          '[Drawing] CloudFront 403 - attempting to refresh cookies...',
        );
        const refreshed = await refreshCloudFrontCookies();
        if (refreshed) {
          console.info('[Drawing] Cookies refreshed - retrying...');
          // Reset fetching flag and retry
          fetchingRef.current = false;
          lastFileIdRef.current = ''; // Force re-fetch
          // The useEffect will trigger again due to dependency change
          return;
        } else {
          console.error('[Drawing] Cookie refresh failed - redirecting to login');
          toast.error('Session expired. Please log in again.');
          localStorage.clear();
          window.location.href = '/';
          return;
        }
      }
      console.error(err);
      fetchingRef.current = false;
      setLoaded(true);
    });
  }, [file.id, map]);

  // Handler for layer visibility change - instant O(1) toggle
  const handleLayerVisibilityChange = useCallback((layerName: string) => {
    setLayerVisibility((prev) => ({
      ...prev,
      [layerName]: !prev[layerName],
    }));
  }, []);

  // Mouse handlers for layer panel
  const handleMouseEnter = useCallback(() => {
    map.scrollWheelZoom.disable();
    map.doubleClickZoom.disable();
    map.dragging.disable();
  }, [map]);

  const handleMouseLeave = useCallback(() => {
    map.scrollWheelZoom.enable();
    map.doubleClickZoom.enable();
    map.dragging.enable();
  }, [map]);

  // Filter layers that have features
  const visibleLayers = useMemo(() => {
    if (!data) return [];
    const featureCounts = new globalThis.Map<string, number>();
    data.features.forEach((f) => {
      const name = f.properties?.layer as string | undefined;
      if (name) featureCounts.set(name, (featureCounts.get(name) || 0) + 1);
    });
    return data.layers.filter(
      (layer) => (featureCounts.get(layer.name) || 0) > 0,
    );
  }, [data]);

  return (
    <>
      {data && (
        <div
          className="checkbox-container"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          Layers:
          {(() => {
            let prevCategory = 'Drawing';
            return visibleLayers.map((layer, index) => {
              let categoryBreak = null;
              if (layer.category && layer.category !== prevCategory) {
                prevCategory = layer.category;
                categoryBreak = (
                  <div
                    key={`cat-${layer.category}-${index}`}
                    style={{
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '1.2em',
                    }}
                  >
                    <br />
                    {layer.category}
                  </div>
                );
              }
              return (
                <React.Fragment key={`layer-${index}-${layer.name}`}>
                  {categoryBreak}
                  <label
                    style={{
                      color: layerColors[layer.name] || '#000',
                      fontSize: '1.2em',
                      textShadow: '0.5px 0.5px 0 #555',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={layerVisibility[layer.name] || false}
                        onChange={() => handleLayerVisibilityChange(layer.name)}
                        style={{
                          accentColor: layerColors[layer.name] || '#000',
                          marginRight: '5px',
                        }}
                      />
                      {layer.name.length >= 35 ? (
                        <Tooltip title={layer.name} placement="right" overlayStyle={{ maxWidth: 500 }}>
                          <span
                            style={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'inline-block',
                              verticalAlign: 'middle',
                              cursor: 'pointer',
                            }}
                          >
                            {`${layer.name.slice(0, 35)}...`}
                          </span>
                        </Tooltip>
                      ) : (
                        <span>{layer.name}</span>
                      )}
                    </div>
                  </label>
                </React.Fragment>
              );
            });
          })()}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        {!loaded && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
            }}
          >
            <Loader />
            {loadingProgress > 0 && loadingProgress < 100 && (
              <div
                style={{ color: '#fff', textAlign: 'center', marginTop: 10 }}
              >
                Loading: {loadingProgress}%
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export const DrawingMap = () => {
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const location = useLocation();
  const [file, setFile] = React.useState();
  
  // Guard against StrictMode double-effect and duplicate fetches
  const fetchedFileIdRef = React.useRef<string | null>(null);

  // Use location.state.id if navigating within app, otherwise use URL param
  const fileId = location.state?.id || id;

  useEffect(() => {
    // Skip if already fetched this file (handles StrictMode double-effect)
    if (fetchedFileIdRef.current === fileId) {
      return;
    }
    
    async function fetchFile() {
      fetchedFileIdRef.current = fileId;
      try {
        const res = await getFile(fileId);
        if (res) {
          setFile(res.data);
        }
      } catch (e) {
        console.error(e);
        // Allow retry on error
        fetchedFileIdRef.current = null;
      }
    }
    fetchFile().catch(console.error);
  }, [fileId]);

  const authUser = AuthUser.getAuthUser();
  if (!authUser) return null;

  const getBreadcrumb = () => {
    // Use projectId from file object (not URL param which is now fileId)
    const projectId = (file as any)?.projectId || id;

    const breadcrumb = [
      {
        title:
          authUser.role === 'admin' ? 'Projects' : 'Dashboard',
        href:
          authUser.role === 'admin'
            ? '/projects-list'
            : '/dashboard',
      },
      {
        title: searchParams.get('projectName'),
        href:
          '/view-project-details/' +
          projectId +
          '?projectName=' +
          searchParams.get('projectName'),
      },
    ];
    const currentBreadcrumb = searchParams.get('breadcrumb');

    if (currentBreadcrumb) {
      const currentBreadcrumbPath = currentBreadcrumb
        .trim()
        .replace(/^>/, '')
        .split('>');

      let setNewBreadcrumb = '';
      currentBreadcrumbPath.forEach((item, i) => {
        setNewBreadcrumb = setNewBreadcrumb + '>' + item;
        const currentCrumb = item.split('-path-');

        const currentProjectName = searchParams.get('projectName');
        if (i + 1 === currentBreadcrumbPath.length) {
          breadcrumb.push({
            title: currentCrumb[0],
            href: '/view-project-details/' + projectId + location.search,
          });
        } else {
          breadcrumb.push({
            title: currentCrumb[0],
            href:
              '/view-project-details/' +
              projectId +
              '?projectName=' +
              currentProjectName +
              '&directory=' +
              currentCrumb[1] +
              '&breadcrumb=' +
              setNewBreadcrumb,
          });
        }
      });
    }

    return breadcrumb;
  };

  const DownloadButton = ({ file }: any) => {
    const [downloading, setDownloading] = React.useState(false);

    const handleDownload = async (type: string) => {
      setDownloading(true);
      try {
        // PDF and DWG exports always use exportAsBuilt (merges original DWG + as-built GeoJSON)
        // This generates the file on-the-fly for both old and new service files
        const response = await exportAsBuilt(file.id, type);
        if (!response || response.status > 299) {
          setDownloading(false);
          toast.error('Download failed');
          return;
        }
        const url = globalThis.URL.createObjectURL(
          new Blob([response.data], { type: response.data.type }),
        );
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers['content-disposition'];
        let filename = `${file.name}_asBuilt.${type}`;
        if (contentDisposition) {
          const matches = /filename="([^"]+)"/.exec(contentDisposition);
          if (matches?.[1]) {
            filename = matches[1];
          }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
      } catch (e) {
        toast.error('Download failed');
        console.error('Error downloading the file:', e);
      } finally {
        setDownloading(false);
      }
    };

    return (
      <div>
        <Button
          className="common-btn button-with-margin"
          onClick={() => handleDownload('pdf')}
        >
          Download PDF
        </Button>
        <Button className="common-btn" onClick={() => handleDownload('dwg')}>
          Download DWG
        </Button>
        <div style={{ height: '1rem' }} />
        {downloading && <Loader />}
      </div>
    );
  };

  return (
    <>
      <Layout className="layout_wrapper">
        <HeaderBar activePopupLink="" />
        <Layout>
          <Sidebar
            currentVal={
              authUser.role === 'user'
                ? 'dashboard'
                : 'projects-list'
            }
          />
          {file ? (
            <Content>
              <Breadcrumb separator=">" items={getBreadcrumb()} />
              <DownloadButton file={file} />
              <MapContainer
                style={{
                  width: '100%',
                  height: '100vh',
                  backgroundColor: 'black',
                }}
                zoom={10}
                id="mapId"
                scrollWheelZoom={true}
                fadeAnimation={true}
                markerZoomAnimation={true}
                // ✅ OPTIMIZATION: Use Canvas renderer instead of SVG (3-5x faster)
                preferCanvas={true}
              >
                <MyData file={file} />
              </MapContainer>
            </Content>
          ) : (
            <Loader />
          )}
        </Layout>
      </Layout>
    </>
  );
};

// Backward compatibility alias
export { DrawingMap as Map };
